import * as React from 'react'
import { View, Text } from 'react-native'
import styled from '@theme/styled-components'

import TextInputComponent from '@components/textInput/TextInput'
import Hoverable from '@components/hover/Hoverable'
import Tag from '@components/tag/Tag'

import UserSelectionWeb from './UserItemSelectionWeb'

import useI18n from '@i18n/useI18n'

interface Props {
  onFilterSelected: (filter: UserFilter) => void
  onReset: () => void
  resetSpecificFilter: (filterName: UserFiltersName) => void
  filters: UserFilters
  onSearchTextChange: (text: string) => void
  searchText: string
  filterItems: UserFilterItems
  initialFilters: UserFilters
  setIsFilterOpen: () => void
  isFilterOpen: boolean
  onHover: (hover: boolean) => void
}

interface Tag {
  label: string
  value: UserFiltersName
}

const UserListFilters = ({
  onFilterSelected,
  onReset,
  resetSpecificFilter,
  filters,
  onSearchTextChange,
  searchText,
  filterItems,
  initialFilters,
  setIsFilterOpen,
  isFilterOpen,
  onHover,
}: Props) => {
  const i18n = useI18n()

  const [selectedFilter, setSelectedFilter] = React.useState<UserFiltersName>()

  React.useEffect(() => {
    if (!isFilterOpen) {
      setSelectedFilter(undefined)
    }
  }, [isFilterOpen])

  const tags: Tag[] = Object.keys(filters).map((key) => ({
    label: i18n.t(`screens.admin.users.filters.${key}`),
    value: key as UserFiltersName,
  }))

  const onTagClicked = (filterName: UserFiltersName) => {
    toggleSelectedFilter(filterName)
  }

  const toggleSelectedFilter = (filter: UserFiltersName) => {
    setSelectedFilter(filter === selectedFilter ? undefined : filter)
    setIsFilterOpen()
  }

  const onFilterUpdate = (filter: UserFilter) => {
    onFilterSelected(filter)
    setSelectedFilter(undefined)
  }

  const getNameForTag = (tag: Tag) => {
    const size = filters[tag.value].length
    const label = `${tag.label} ${size ? `: ${size}` : ''}`

    return { label, value: tag.value }
  }

  const renderTagItems = (tag: Tag, index: number) => {
    const selected = filters[tag.value].length > 0

    return (
      <Hoverable onHover={onHover} key={tag.value}>
        <TagItem index={index}>
          <TagContainer key={tag.label}>
            <Tag
              item={getNameForTag(tag)}
              onItemSelected={() => onTagClicked(tag.value)}
              selected={selected}
              inactiveColor
            />
          </TagContainer>
          {!!selectedFilter && selectedFilter === tag.value && (
            <FilterOverView>
              <UserSelectionWeb
                selectedFilter={selectedFilter}
                addFilter={onFilterUpdate}
                filters={filters}
                filterItems={filterItems[tag.value]}
                onClose={() => setSelectedFilter(undefined)}
                resetSpecificFilter={resetSpecificFilter}
                initialFilters={initialFilters}
              />
            </FilterOverView>
          )}
        </TagItem>
      </Hoverable>
    )
  }

  return (
    <MainContainer>
      <TextInputComponent
        onChange={onSearchTextChange}
        placeholder={i18n.t('screens.incidentList.search')}
        left={{ name: 'search' }}
        value={searchText}
      />
      <Container>
        <TagView>
          {tags.map(renderTagItems)}
          <ResetText onPress={onReset}>{i18n.t('screens.incidentList.filters.reset')}</ResetText>
        </TagView>
      </Container>
    </MainContainer>
  )
}

const MainContainer = styled(View)`
  margin-top: 32px;
  flex-direction: column-reverse;
`

const Container = styled(View)`
  flex-direction: row;
  justify-content: space-between;
`

const TagView = styled(View)`
  flex-flow: row wrap;
  max-width: 70%;
  align-items: center;
`

const TagItem = styled(View)<{ index: number }>`
  z-index: ${(props) => -props.index};
`

const TagContainer = styled(View)`
  margin-right: 8px;
  margin-bottom: 8px;
  margin-top: 8px;
`

const ResetText = styled(Text)`
  ${(props) => props.theme.fonts.Body};
  font-size: 16px;
  text-decoration: underline;
  margin: 16px 0;
  z-index: -10;
`

const FilterOverView = styled(View)`
  position: absolute;
  background-color: ${(props) => props.theme.colors.background};
  border-radius: 8px;
  top: 60px;
  ${(props) => props.theme.constants.shadow};
  min-width: 350px;
  height: 500px;
`

export default UserListFilters
